"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface AdvancedFeature {
  title: string;
  body: string;
  icon: LucideIcon;
  accent: string;
}

export interface AdvancedFeaturesSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  features: AdvancedFeature[];
  sectionAccent: string;
}

export function AdvancedFeaturesSection({
  id = "advanced-features",
  eyebrow,
  title,
  features,
  sectionAccent,
}: AdvancedFeaturesSectionProps) {
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute bottom-1/4 left-1/4 h-80 w-80 rounded-full opacity-15 blur-[120px]"
          style={{ background: sectionAccent }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: sectionAccent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className="mt-12 space-y-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            const reverse = i % 2 === 1;
            return (
              <Reveal key={f.title} delay={i * 80}>
                <div
                  className={`glass rounded-2xl p-7 flex flex-col md:flex-row gap-6 ${reverse ? "md:flex-row-reverse" : ""}`}
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${f.accent} 14%, transparent)`,
                  }}
                >
                  <div className="shrink-0">
                    <span
                      className="grid place-items-center h-14 w-14 rounded-2xl"
                      style={{
                        background: `color-mix(in oklch, ${f.accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${f.accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-6 w-6" style={{ color: f.accent }} />
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="eyebrow-tiny" style={{ color: f.accent }}>
                      Advanced {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-1">{f.title}</h3>
                    <p className="mt-3 text-body-sm text-[rgb(var(--fg-muted))] leading-relaxed">{f.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
